import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PaperCard } from "@/components/PaperCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search } from "lucide-react";

export default function Articles() {
  const [cats, setCats] = useState([]);
  const [papers, setPapers] = useState([]);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [limit, setLimit] = useState(12);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (cat !== "all") params.set("category", cat);
    params.set("limit", String(limit));
    api.get(`/papers?${params.toString()}`).then(({ data }) => setPapers(data)).finally(() => setLoading(false));
  }, [query, cat, limit]);

  useEffect(() => { load(); }, [load]);

  const submit = (e) => { e.preventDefault(); setLimit(12); setQuery(q.trim()); };

  return (
    <div>
      <PageHeader eyebrow="Open Access Library" title="Articles" subtitle="Explore peer-reviewed research across every discipline — read, download and cite freely." breadcrumb="Articles" />

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <form onSubmit={submit} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="articles-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, keyword or author…" className="pl-11 h-11 rounded-full" />
            </div>
            <Button type="submit" className="h-11 px-6 rounded-full" data-testid="articles-search-btn">Search</Button>
          </form>
          <Select value={cat} onValueChange={(v) => { setCat(v); setLimit(12); }}>
            <SelectTrigger className="h-11 md:w-64 rounded-full" data-testid="articles-category"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : papers.length === 0 ? (
          <div className="text-center py-24 text-slate-500">No articles found. Try a different search or subject.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {papers.map((p) => <PaperCard key={p.id} paper={p} />)}
            </div>
            {papers.length >= limit && (
              <div className="text-center mt-10">
                <Button variant="outline" className="rounded-full" onClick={() => setLimit(limit + 12)} data-testid="articles-load-more">Load more articles</Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
